import { Component } from "@theme/component";
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
import { requestIdleCallback, requestYieldCallback } from "@theme/utilities";
import { storefrontEvents } from "@theme/storefront-events";

/**
 * Global cache shared across all variant picker instances
 * This allows cache to persist when switching between products in combined listings
 *
 * HYBRID APPROACH:
 * - Cache preloaded variants for instant switching (no network delay)
 * - Fetch lightweight JSON for uncached variants (faster than full HTML)
 * - Fallback to full HTML when variant ID not available
 * - Preload connected products' variants in background for instant combine listing switches
 */
const globalVariantsCache = new Map(); // "productId|option1|option2" → variant
const globalMediaCache = new Map(); // "productId|mediaId" → media
const globalVariantByIdCache = new Map(); // "variantId" → variant  (combine listing O(1) lookup)
const globalProductIdCache = new Map(); // "productUrl" → productId (combine listing)

const LINE_ITEM_FIELDS = ".line-item-property-block :is(input, textarea, select)";

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #jsonAbortController;

  /** @type {string | undefined} */
  #lastProductId;

  /** Monotonic token so aborted/outdated section fetches don't clear the fetching state too early. */
  /** @type {number} */
  #variantFetchGeneration = 0;

  /** The `shopify:product:select` of the latest selection, until it settles. */
  /** @type {any} */
  #pendingProductSelect = null;

  /** Every variant of the product, when the variants cache holds all of them (see #updateOptionAvailability). */
  /** @type {any[] | null} */
  #allVariants = null;

  connectedCallback() {
    super.connectedCallback();

    // Store initial product ID
    this.#lastProductId = this.dataset.productId;

    // HYBRID: Load and cache preloaded variants for instant switching
    this.#loadVariantsCache();

    // Preload connected products' variants in background for instant combine listing switches
    this.#loadConnectedProductsCache();

    this.addEventListener("change", this.variantChanged.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Abort any pending fetch requests to prevent memory leaks
    this.#abortController?.abort();
    this.#jsonAbortController?.abort();
    this.removeEventListener("change", this.variantChanged.bind(this));
  }

  updatedCallback() {
    super.updatedCallback?.();

    // When product changes (combine listing), re-load cache for new product
    const currentProductId = this.dataset.productId;
    if (currentProductId !== this.#lastProductId) {
      this.#lastProductId = currentProductId;
      // HYBRID: Re-load cache for new product
      this.#loadVariantsCache();
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    const fetchToken = this.beginVariantFetch();

    this.updateSelectedOption(event.target);
    const productSelect = this.startProductSelect();
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? "",
      })
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === "true" &&
      !event.target.closest("product-card") &&
      !event.target.closest("quick-add-dialog");

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    // The connected product URL can carry `?variant=` (after a morph): compare and navigate by path only.
    const newUrl = selectedOption.dataset.connectedProductUrl?.split("?")[0] || undefined;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // HYBRID APPROACH: Cache first, then JSON fetch, then full HTML fallback
    const variantId = selectedOption.dataset.variantId;
    const cachedVariant = this.#getCachedVariant();

    // newUrl / currentUrl already computed above (lines 103-104)
    const hasProductChanged = !!newUrl && currentUrl !== newUrl;

    if (!hasProductChanged) this.#updateOptionAvailability();

    if (hasProductChanged) {
      // COMBINE LISTING PRODUCT SWITCH: Try P2's preloaded cache first (populated by
      // #loadConnectedProductsCache if the idle fetch completed before the user clicked).
      // Fall back to immediate HTML fetch (no idle delay) when not yet preloaded.
      const cleanNewUrl = newUrl.replace(/\?.*$/, "");
      const cachedP2Variant = variantId ? globalVariantByIdCache.get(variantId) : null;
      const p2ProductId = globalProductIdCache.get(cleanNewUrl);

      if (cachedP2Variant && p2ProductId) {
        // ⚡ P2 preloaded: instant optimistic update + background HTML for morph
        this.#updateCriticalUI(cachedP2Variant, true);
        this.#resolveProductSelect(productSelect, cachedP2Variant);
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(cachedP2Variant, this.selectedOptionId, {
              html: null,
              productId: p2ProductId,
              newProduct: { id: p2ProductId, url: selectedOption.dataset.connectedProductUrl || newUrl },
              isOptimistic: true,
            })
          );
        }
        this.#syncInBackground(selectedOption, loadsNewProduct, fetchToken, productSelect);
      } else {
        // P2 not preloaded yet — fetch HTML immediately (no idle delay, no stale data risk)
        this.fetchUpdatedSection(
          this.buildRequestUrl(selectedOption),
          loadsNewProduct,
          false,
          fetchToken,
          productSelect
        );
      }
    } else if (cachedVariant) {
      // ⚡ CACHE HIT: Instant update with no network delay
      this.#updateCriticalUI(cachedVariant, true);
      this.#resolveProductSelect(productSelect, cachedVariant);

      // Dispatch event immediately for other components
      if (this.selectedOptionId) {
        this.dispatchEvent(
          new VariantUpdateEvent(cachedVariant, this.selectedOptionId, {
            html: null,
            productId: this.dataset.productId ?? "",
            isOptimistic: true,
          })
        );
      }

      // Still fetch HTML in background for morph (custom elements, etc.)
      this.#syncInBackground(selectedOption, loadsNewProduct, fetchToken, productSelect);
    } else if (variantId) {
      // CACHE MISS: Fetch lightweight JSON (faster than full HTML)
      this.#fetchVariantJson(variantId)
        .then((variantData) => {
          if (variantData) {
            // Update UI with fetched JSON data
            this.#updateCriticalUI(variantData, true);
            this.#resolveProductSelect(productSelect, variantData);

            // Dispatch event immediately for other components
            if (this.selectedOptionId) {
              this.dispatchEvent(
                new VariantUpdateEvent(variantData, this.selectedOptionId, {
                  html: null,
                  productId: this.dataset.productId ?? "",
                  isOptimistic: true,
                })
              );
            }
          }
        })
        .catch((error) => {
          console.warn("Failed to fetch variant JSON:", error);
        });

      // Fetch full HTML in background for morph (custom elements, etc.)
      this.#syncInBackground(selectedOption, loadsNewProduct, fetchToken, productSelect);
    } else {
      // FALLBACK: No variant ID - fetch full HTML as fallback
      this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct, false, fetchToken, productSelect);
    }

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      // The option's `data-variant-id` was rendered for the previous selection (until the background sync
      // re-renders the picker): the cache resolves the variant from the selected values instead.
      const urlVariantId = cachedVariant ? String(cachedVariant.id) : variantId;
      if (urlVariantId) {
        url.searchParams.set("variant", urlVariantId);
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, "", url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }

    // Show the selected value next to the option name now: the section HTML that renders it comes later.
    if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) {
      const labelValue = target.closest(".variant-option")?.querySelector(".variant-option__label-value");
      if (labelValue) labelValue.textContent = (target instanceof HTMLSelectElement ? target.selectedOptions[0]?.dataset.dvLabel : target.dataset.dvLabel) ?? target.value;
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest("quick-add-dialog") || this.closest("swatches-variant-picker")) {
      let sectionId = this.quickAddDrawerId;
      if (this.closest("swatches-variant-picker")) {
        sectionId = "section-rendering-product-card";
      }
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=${sectionId}&${params.join("&")}`;
    }
    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * @param {boolean} isFetching - Whether section HTML is being fetched.
   */
  #setVariantFetching(isFetching) {
    this.classList.toggle("is-variant-fetching", isFetching);
  }

  /**
   * Marks this picker as fetching section HTML until the matching request completes.
   * Used with CSS (e.g. `:has(variant-picker.is-variant-fetching)`) for dependent UI like spinners.
   * @returns {number} Token to pass to `fetchUpdatedSection` so only the latest fetch clears the class.
   */
  beginVariantFetch() {
    const fetchToken = ++this.#variantFetchGeneration;
    this.#setVariantFetching(true);
    return fetchToken;
  }

  /**
   * Fetches the section HTML for a selection already shown from the cache or variant JSON, when the browser
   * is idle. A newer selection makes it outdated: skip it before `buildRequestUrl()` records a pending URL.
   * @param {HTMLElement} selectedOption
   * @param {boolean} loadsNewProduct
   * @param {number} fetchToken
   * @param {any} productSelect
   */
  #syncInBackground(selectedOption, loadsNewProduct, fetchToken, productSelect) {
    requestIdleCallback(() => {
      if (fetchToken !== this.#variantFetchGeneration) {
        this.#rejectProductSelect(productSelect, new DOMException("Superseded by a newer selection", "AbortError"));
        return;
      }
      this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct, true, fetchToken, productSelect);
    });
  }

  /**
   * Dispatches `shopify:product:select` for the current selection. A newer selection supersedes the
   * previous one, whose promise then rejects with an `AbortError` instead of hanging.
   * @returns {any} The deferred to settle, or null.
   */
  startProductSelect() {
    this.#rejectProductSelect(
      this.#pendingProductSelect,
      new DOMException("Superseded by a newer selection", "AbortError")
    );

    const optionNames = this.#optionNames;
    const selection = Array.from(
      this.querySelectorAll("select option[selected], .variant-option--fieldset input:checked"),
      (element, index) => {
        const input = /** @type {HTMLInputElement | HTMLOptionElement} */ (element);
        // `data-input-id` is "<option position>-<value index>".
        const position = Number(input.dataset.inputId?.split("-")[0]) || index + 1;
        const name =
          /** @type {HTMLElement | null} */ (input.closest("[data-option-name]"))?.dataset.optionName ??
          optionNames[position - 1] ??
          "";
        return { position, name, value: input.value };
      }
    );
    if (!selection.length) return null;

    const productSelect = storefrontEvents.start(this, "ProductSelectEvent", {
      product: {
        id: this.dataset.productId ?? "",
        title: this.dataset.productTitle ?? "",
        handle: this.dataset.productHandle ?? "",
      },
      selectedOptions: selection.map(({ name, value }) => ({ name, value })),
    });
    if (productSelect) productSelect.selection = selection;
    this.#pendingProductSelect = productSelect;
    return productSelect;
  }

  /** @returns {string[]} The product's option names in position order. */
  get #optionNames() {
    try {
      const names = JSON.parse(this.dataset.optionNames || "[]");
      if (Array.isArray(names) && names.length) return names;
    } catch {}
    return Array.from(
      this.querySelectorAll("[data-option-name]"),
      (element) => /** @type {HTMLElement} */ (element).dataset.optionName ?? ""
    );
  }

  /**
   * Resolves a `shopify:product:select` with the variant the options map to, or null when none does.
   * @param {any} productSelect
   * @param {any} variant - Variant JSON (Liquid or `/products/<handle>.json` shape).
   */
  #resolveProductSelect(productSelect, variant) {
    if (!productSelect || productSelect.settled) return;
    // A preloaded `/products/<handle>.json` variant (combined listing) has no `available`: leave the event
    // to the section HTML fetched for the same selection, whose Liquid JSON has it.
    if (variant && typeof variant.available !== "boolean") return;
    productSelect.settled = true;

    // The rendered variant JSON falls back to the first available variant when the selected
    // combination doesn't exist: only report a variant whose options match the selection.
    const values = variant?.options || [variant?.option1, variant?.option2, variant?.option3];
    const matches =
      !!variant &&
      (productSelect.selection || []).every(
        (/** @type {{ position: number, value: string }} */ option) => values[option.position - 1] === option.value
      );

    productSelect.resolve({
      variant: matches ? storefrontEvents.toVariant(variant, this.#optionNames) : null,
    });
    if (this.#pendingProductSelect === productSelect) this.#pendingProductSelect = null;
  }

  /**
   * The selected variant JSON rendered in a section response for this picker (main or card swatches).
   * @param {Document} html
   * @returns {any}
   */
  #extractSelectedVariant(html) {
    const script = html.querySelector(
      `${this.tagName.toLowerCase()} script[type="application/json"]:not([data-variants-cache])`
    );
    try {
      return script?.textContent ? JSON.parse(script.textContent) : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {any} productSelect
   * @param {any} error
   */
  #rejectProductSelect(productSelect, error) {
    if (!productSelect || productSelect.settled) return;
    productSelect.settled = true;
    productSelect.reject(error);
    if (this.#pendingProductSelect === productSelect) this.#pendingProductSelect = null;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   * @param {boolean} isBackgroundSync - If this is a background sync after cache hit (don't dispatch events again)
   * @param {number} [fetchToken] - When set, clears fetching state only if still the latest fetch (see {@link beginVariantFetch}).
   * @param {any} [productSelect] - The selection's `shopify:product:select` deferred (see {@link startProductSelect}).
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false, isBackgroundSync = false, fetchToken, productSelect) {
    // A newer selection has started its own fetch: this one (typically an idle background sync scheduled
    // before it) is outdated and must not abort the newer request.
    if (fetchToken !== undefined && fetchToken !== this.#variantFetchGeneration) {
      this.#rejectProductSelect(productSelect, new DOMException("Superseded by a newer selection", "AbortError"));
      return;
    }

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    fetch(requestUrl, { signal })
      .then((response) => response.text())
      .then((responseText) => {
        // Guard against stale responses: if a newer fetch started and aborted this
        // controller, the response arrived after the abort (race condition).
        // Checking signal.aborted here catches that case even when the HTTP response
        // was already received before abort() was called.
        if (signal.aborted) {
          this.#rejectProductSelect(productSelect, new DOMException("Superseded by a newer selection", "AbortError"));
          return;
        }

        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(responseText, "text/html");
        const variantData = this.#extractVariantData(html);
        // No-op when the cache or the variant JSON already answered.
        this.#resolveProductSelect(productSelect, this.#extractSelectedVariant(html));

        if (variantData) {
          if (!isBackgroundSync) {
            // Update critical UI immediately (only if not from cache)
            // Note: Media is NOT updated here - will be replaced via HTML morph
            this.#updateCriticalUI(variantData, false);
          }
        } else {
          console.warn("No variant data extracted - dispatching without optimistic update");
        }

        // Morph HTML (will happen after critical UI update)
        if (shouldMorphMain) {
          // Capture context BEFORE morph — variant-picker may be removed if new product has only a default variant.
          const section = this.closest(".shopify-section, dialog");
          const savedOptionId = this.selectedOptionId;
          const newVPSource = html.querySelector(this.tagName.toLowerCase());
          let newProductForEvent;
          if (newVPSource instanceof HTMLElement) {
            const newProductId = newVPSource.dataset.productId;
            const newProductUrl = newVPSource.dataset.productUrl;
            if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
              newProductForEvent = { id: newProductId, url: newProductUrl };
            }
          } else {
            // No variant-picker in response (product has only default variant).
            // Detect product switch via product-price element instead.
            const newPriceEl = html.querySelector("product-price");
            const newProductId = newPriceEl instanceof HTMLElement ? newPriceEl.dataset.productId : undefined;
            if (newProductId && this.dataset.productId !== newProductId) {
              newProductForEvent = { id: newProductId, url: "" };
            }
          }

          this.updateMain(html);

          // Dispatch after morph so components (e.g. product-price) can react to the new variant state.
          // Use section as dispatcher since variant-picker may have been disconnected by the morph.
          if (section) {
            section.dispatchEvent(
              new VariantUpdateEvent(variantData || null, savedOptionId ?? "", {
                html,
                productId: newProductForEvent?.id ?? this.dataset.productId ?? "",
                newProduct: newProductForEvent,
                isOptimistic: false,
                isBackgroundSync: false,
              })
            );
          }
        } else {
          const newProduct = this.updateVariantPicker(html);

          // Always dispatch event with HTML (even without variantData or for background sync)
          // Components need HTML to morph elements (button text, inventory, badge)
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variantData || null, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? "",
                newProduct,
                isOptimistic: false,
                isBackgroundSync, // Flag to indicate this is background sync
              })
            );
          }
        }
      })
      .catch((error) => {
        this.#rejectProductSelect(productSelect, error);
        if (error.name === "AbortError") {
          console.warn("Fetch aborted by user");
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        if (fetchToken !== undefined && fetchToken === this.#variantFetchGeneration) {
          this.#setVariantFetching(false);
        }
      });
  }

  /**
   * Fetch lightweight JSON data for a specific variant
   * This is faster than fetching full HTML (smaller payload)
   *
   * @param {string} variantId - The variant ID to fetch
   * @returns {Promise<Object|null>} The variant data or null
   */
  async #fetchVariantJson(variantId) {
    if (!variantId) return null;

    try {
      // Build URL to fetch variant-data section
      const productUrl = this.dataset.productUrl || window.location.pathname;
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("sections", "variant-data");
      // By the selected option values, like the section HTML: the option's `data-variant-id` can still be the
      // previous selection's. sections/variant-data.liquid falls back to `selected_or_first_available_variant`,
      // which Shopify resolves from `option_values`.
      url.searchParams.set("option_values", this.selectedOptionsValues.join(","));

      // Abort previous JSON fetch if still pending (rapid clicking)
      // Uses separate controller so background HTML fetch doesn't abort this
      this.#jsonAbortController?.abort();
      this.#jsonAbortController = new AbortController();

      // Fetch section (Shopify wraps in HTML, not pure JSON)
      const response = await fetch(url.toString(), { signal: this.#jsonAbortController.signal });

      if (!response.ok) {
        console.warn(`Variant JSON fetch failed: ${response.status}`);
        return null;
      }

      // Section Rendering API returns: { "variant-data": "HTML string" }
      const data = await response.json();
      const htmlString = data["variant-data"];

      if (!htmlString) {
        console.warn("No variant-data in response");
        return null;
      }

      // Parse HTML to extract JSON from section content
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlString;
      const sectionDiv = tempDiv.querySelector("#shopify-section-variant-data");

      if (!sectionDiv?.textContent) {
        console.warn("Could not extract JSON from section");
        return null;
      }

      // Parse the JSON from section content
      const variantData = JSON.parse(sectionDiv.textContent);

      return variantData;
    } catch (error) {
      // Silent abort errors (user clicked another variant)
      if (error.name === "AbortError") return null;

      console.error("Error fetching variant JSON:", error);
      return null;
    }
  }

  /**
   * Extract variant data from HTML response
   * @param {Document} html - The parsed HTML document
   * @returns {Object | null} The variant data or null if not found
   */
  #extractVariantData(html) {
    const scriptTag = html.querySelector('variant-picker script[type="application/json"]');

    if (!scriptTag?.textContent) {
      console.warn("No variant data found in response");
      return null;
    }

    try {
      return JSON.parse(scriptTag.textContent);
    } catch (e) {
      console.error("Failed to parse variant data:", e);
      return null;
    }
  }

  /**
   * Update critical UI elements immediately with variant data.
   * Only called for same-product variant switches (combine listing uses fetchUpdatedSection directly).
   * @param {Object} variantData - The variant object from JSON
   * @param {boolean} isFromCache - Whether this update is from cache (true) or from server (false)
   */
  #updateCriticalUI(variantData, isFromCache = false) {
    // 1. Update price immediately
    this.#updatePriceOptimistic(variantData);

    // 2. Update media gallery (only for cache hit — server fetch replaces entire gallery via morph)
    if (isFromCache) {
      this.#updateMediaOptimistic(variantData);
    }

    // 3. Update button state
    this.#updateButtonStateOptimistic(variantData);

    document.dispatchEvent(
      new CustomEvent(ThemeEvents.variantChanged, {
        detail: { variant: variantData },
      })
    );
  }

  /**
   * Update price optimistically from variant data
   * @param {Object} variant - The variant object
   */
  #updatePriceOptimistic(variant) {
    // IMPORTANT: Scope to product-card first to avoid affecting other cards in the same section
    const scope = this.closest("product-card") || this.closest(".shopify-section, dialog");
    const priceComponent = scope?.querySelector("product-price");

    if (!priceComponent) return;

    // Dispatch optimistic update event
    priceComponent.dispatchEvent(
      new CustomEvent("price:update-optimistic", {
        detail: { variant },
        bubbles: false,
      })
    );
  }

  /**
   * Update media gallery optimistically
   * @param {Object} variant - The variant object
   */
  #updateMediaOptimistic(variant) {
    if (!variant.featured_media) return;

    // IMPORTANT: Scope to product-card first to avoid affecting other cards in the same section
    const scope = this.closest("product-card") || this.closest(".shopify-section, dialog");
    const mediaGallery = scope?.querySelector("media-gallery");

    if (!mediaGallery) return;

    // Dispatch optimistic media switch event
    mediaGallery.dispatchEvent(
      new CustomEvent("media:switch-optimistic", {
        detail: { variant },
        bubbles: false,
      })
    );
  }

  /**
   * Update button state optimistically
   * @param {Object} variant - The variant object
   */
  #updateButtonStateOptimistic(variant) {
    // IMPORTANT: Scope to product-card first to avoid affecting other cards in the same section
    const scope = this.closest("product-card") || this.closest(".shopify-section, dialog");
    const productForm = scope?.querySelector("product-form-component");

    if (!productForm) return;

    const addToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    if (!addToCartButton) return;

    // Update button state based on availability
    addToCartButton.disabled = !variant.available;
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    if (!newHtml) return;

    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    // For combine listing switches in quick-add, morph the quantity selector block so
    // structural changes (volume pricing appearing/disappearing) are reflected correctly.
    // On the product page, combine listing uses updateMain instead, so newProduct is never
    // set here in that context.
    if (newProduct) {
      const sectionId = this.dataset.sectionId;
      if (sectionId) {
        const newWrapper = newHtml.getElementById(`QuantitySelectorWrapper-${sectionId}`);
        const curWrapper = document.getElementById(`QuantitySelectorWrapper-${sectionId}`);
        if (newWrapper && curWrapper) {
          // morph() uses childrenOnly:true by default, skipping element attributes.
          // Manually sync the class attribute so flex layout updates correctly
          // (wrapper needs class="quantity-selector" removed when switching to a
          // product with volume pricing, and added back when switching away).
          const newClass = newWrapper.getAttribute("class");
          if (newClass) {
            curWrapper.setAttribute("class", newClass);
          } else {
            curWrapper.removeAttribute("class");
          }
          morph(curWrapper, newWrapper);
        }
      }
    }

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    const savedProperties = this.#captureLineItemProperties(main);
    morph(main, newMain);
    this.#restoreLineItemProperties(main, savedProperties);
  }

  /**
   * Snapshots what the customer entered in the line item property fields, keyed by "<type>:<name>".
   * Hidden inputs are skipped: the checkbox companion input must keep the value from the new HTML.
   *
   * @param {Element} main
   * @returns {Map<string, string | boolean>}
   */
  #captureLineItemProperties(main) {
    const saved = new Map();

    for (const el of main.querySelectorAll(LINE_ITEM_FIELDS)) {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) {
        continue;
      }
      if (!el.name || el.type === "hidden") continue;

      const key = `${el.type}:${el.name}`;
      if (el.type === "checkbox") {
        saved.set(key, /** @type {HTMLInputElement} */ (el).checked);
      } else if (el.type === "radio") {
        if (/** @type {HTMLInputElement} */ (el).checked) saved.set(key, el.value);
      } else {
        saved.set(key, el.value);
      }
    }

    return saved;
  }

  /**
   * Re-applies the snapshot after the morph reset the fields to their defaults. Fields that no longer exist,
   * changed type, or lost the chosen option keep the new product's default.
   *
   * @param {Element} main
   * @param {Map<string, string | boolean>} saved
   */
  #restoreLineItemProperties(main, saved) {
    if (saved.size === 0) return;

    for (const el of main.querySelectorAll(LINE_ITEM_FIELDS)) {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) {
        continue;
      }

      const key = `${el.type}:${el.name}`;
      if (!el.name || !saved.has(key)) continue;
      const value = saved.get(key);

      if (el instanceof HTMLInputElement && el.type === "checkbox") {
        el.checked = value === true;
      } else if (el instanceof HTMLInputElement && el.type === "radio") {
        if (el.value === value) el.checked = true;
      } else if (el instanceof HTMLSelectElement) {
        // Assigning a missing value would leave the select blank (selectedIndex -1).
        if (Array.from(el.options).some((option) => option.value === value)) el.value = String(value);
      } else if (typeof value === "string") {
        el.value = value;
      }
    }
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector("select option[selected], .variant-option--fieldset input:checked");

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], .variant-option--fieldset input:checked")
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }

  get quickAddDrawerId() {
    const quickAddDrawer = document.getElementById("quick-add-drawer");
    return quickAddDrawer?.dataset.sectionId;
  }

  /**
   * HYBRID APPROACH: Load and cache preloaded variants for instant switching
   * Variants are preloaded in the initial HTML for zero-latency updates
   */
  #loadVariantsCache() {
    const cacheScript = this.querySelector("script[data-variants-cache]");

    if (!cacheScript?.textContent) {
      console.warn("⚠️ No variants cache found - will fetch from server");
      return;
    }

    const productId = this.dataset.productId;
    if (!productId) {
      console.warn("No productId found - cannot cache variants");
      return;
    }

    try {
      const data = JSON.parse(cacheScript.textContent);

      this.#allVariants =
        Array.isArray(data.variants) && data.variants.length === data.variantsCount ? data.variants : null;

      // Cache variants indexed by productId + option values for fast lookup
      if (data.variants && Array.isArray(data.variants)) {
        data.variants.forEach((variant) => {
          const key = this.#buildCacheKey(variant);
          globalVariantsCache.set(key, variant);
          // Also index by variant ID for O(1) combine listing lookup
          globalVariantByIdCache.set(String(variant.id), variant);
        });
      }

      // Cache media for quick reference (with productId prefix)
      if (data.media && Array.isArray(data.media)) {
        data.media.forEach((media) => {
          const mediaKey = `${productId}|${media.id}`;
          globalMediaCache.set(mediaKey, media);
        });
      }
    } catch (e) {
      console.error("Failed to load variants cache:", e);
    }
  }

  /**
   * Schedules background preload of all connected products' variants for instant combine listing switches.
   * Only fetches URLs not already in globalProductIdCache (idempotent across multiple VP instances).
   */
  #loadConnectedProductsCache() {
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const connectedUrls = new Set(
      [...this.querySelectorAll("[data-connected-product-url]")]
        .map((el) => el.dataset.connectedProductUrl)
        .filter((url) => url && url !== currentUrl && !globalProductIdCache.has(url.replace(/\?.*$/, "")))
    );

    if (connectedUrls.size === 0) return;

    // Mark URLs as pending immediately (null sentinel) so other VP instances on the same page
    // (e.g. quick-add drawer VP) don't schedule duplicate fetches for the same URL.
    connectedUrls.forEach((url) => globalProductIdCache.set(url.replace(/\?.*$/, ""), null));

    requestIdleCallback(() => {
      connectedUrls.forEach((url) => this.#preloadConnectedProductCache(url));
    });
  }

  /**
   * Fetches a connected product's variants via /products/handle.json and populates the global caches.
   * Silent fail — preload is best-effort; fallback is always the HTML fetch path.
   * @param {string} productUrl - The connected product URL (e.g. /products/handle)
   */
  async #preloadConnectedProductCache(productUrl) {
    try {
      const cleanUrl = productUrl.replace(/\?.*$/, "");
      const response = await fetch(`${cleanUrl}.json`);
      if (!response.ok) return;

      const { product } = await response.json();
      if (!product?.id || !Array.isArray(product.variants)) return;

      const productId = String(product.id);
      globalProductIdCache.set(cleanUrl, productId);

      product.variants.forEach((variant) => {
        // Index by variant ID for O(1) lookup during combine listing switch
        globalVariantByIdCache.set(String(variant.id), variant);

        // Index by options (same format as #loadVariantsCache) for post-switch re-lookup
        const parts = [productId];
        if (variant.option1) parts.push(variant.option1);
        if (variant.option2) parts.push(variant.option2);
        if (variant.option3) parts.push(variant.option3);
        globalVariantsCache.set(parts.join("|"), variant);
      });
    } catch {
      // Silent fail — preload is best-effort
    }
  }

  /**
   * Marks the option values available or not for the new selection, before the section HTML that renders them
   * arrives. Same rule as `product_option_value.available`: a value is available when an available variant has
   * it along with the values selected for the previous options.
   * Skipped when the cache doesn't hold every variant, for combined listings (availability comes from the other
   * products) and for pickers that don't show every option (card swatches).
   */
  #updateOptionAvailability() {
    const variants = this.#allVariants;
    if (!variants?.length) return;

    const groups = Array.from(this.querySelectorAll(".variant-option"));
    if (groups.length !== variants[0].options?.length) return;
    if (
      Array.from(this.querySelectorAll("[data-connected-product-url]")).some((el) => el.dataset.connectedProductUrl)
    ) {
      return;
    }

    const selectedValues = groups.map(
      (group) =>
        /** @type {HTMLSelectElement | HTMLInputElement | null} */ (
          group.querySelector("select, input[type='radio']:checked")
        )?.value
    );

    groups.forEach((group, position) => {
      const availableValues = new Set(
        variants
          .filter(
            (variant) =>
              variant.available &&
              selectedValues.slice(0, position).every((value, index) => variant.options[index] === value)
          )
          .map((variant) => variant.options[position])
      );

      for (const input of group.querySelectorAll("input[type='radio']")) {
        if (!(input instanceof HTMLInputElement)) continue;
        const available = availableValues.has(input.value);
        input.dataset.optionAvailable = String(available);

        const label = input.closest("label");
        const strikethrough = label?.querySelector(".strikethrough-variant");
        if (available) {
          input.removeAttribute("aria-disabled");
          strikethrough?.remove();
        } else {
          input.setAttribute("aria-disabled", "true");
          if (label && !strikethrough) {
            const line = document.createElement("div");
            line.className = "strikethrough-variant";
            label.append(line);
          }
        }
      }

      const select = group.querySelector("select");
      if (select instanceof HTMLSelectElement) {
        for (const option of select.options) {
          const available = availableValues.has(option.value);
          option.dataset.optionAvailable = String(available);
          const displayValue = option.dataset.dvLabel ?? option.value;
          option.textContent = available ? displayValue : `${displayValue} - ${select.dataset.unavailableText ?? ""}`;
        }
      }
    });
  }

  #buildCacheKey(variant) {
    const productId = this.dataset.productId;
    if (!productId) {
      console.warn("No productId for cache key");
      return "";
    }

    // Build key from productId + available options (option1, option2, option3)
    const parts = [productId];
    if (variant.option1) parts.push(variant.option1);
    if (variant.option2) parts.push(variant.option2);
    if (variant.option3) parts.push(variant.option3);
    return parts.join("|");
  }

  #getCachedVariant() {
    const productId = this.dataset.productId;
    if (!productId) {
      console.warn("No productId - cannot get cached variant");
      return null;
    }

    // Get currently selected option values (scoped to this variant-picker instance)
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], .variant-option--fieldset input:checked")
    );

    if (selectedOptions.length === 0) return null;

    // Build cache key from productId + selected values
    const optionValues = selectedOptions.map((el) => el.value).join("|");
    const key = `${productId}|${optionValues}`;

    return globalVariantsCache.get(key) || null;
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
